import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { CheckCircle2, MessageSquare, Pencil, Pin, Trash2 } from 'lucide-react';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { formatDate } from '../../utils/format';
import { getErrorMessage } from '../../lib/apiClient';
import { useAuthStore } from '../../store/authStore';
import {
  commentKeys,
  createComment,
  deleteComment,
  getThread,
  listComments,
  mentionableUsers,
  pinComment,
  reopenComment,
  resolveComment,
  unpinComment,
  updateComment,
} from './commentsApi';

/** Debounce a value by `delay` ms. */
function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// An "@" at the start of a word plus whatever's been typed after it, ending at
// the caret — emails (`a@b.com`) never match since their `@` follows a word.
const MENTION_TOKEN_RE = /(?:^|\s)@([\w.-]*)$/;

function detectMentionToken(value, caret) {
  const match = MENTION_TOKEN_RE.exec(value.slice(0, caret));
  if (!match) return null;
  return { token: match[1], atIndex: caret - match[1].length - 1 };
}

/**
 * Turn the final body into `{ userId, startOffset, endOffset }` spans by
 * scanning for the names picked via the popup. Deriving offsets at submit
 * (instead of tracking them live) keeps them correct through any later edits;
 * a deleted or mangled name simply stops matching. Longest name first so a
 * chosen "Ann" never claims the front of "Anna Lee".
 */
function deriveMentions(body, chosen) {
  const names = Object.keys(chosen).sort((a, b) => b.length - a.length);
  if (!names.length) return [];
  const out = [];
  let i = body.indexOf('@');
  while (i !== -1) {
    const atWordStart = i === 0 || /\s/.test(body[i - 1]);
    const name = atWordStart
      ? names.find((n) => {
          if (!body.startsWith(n, i + 1)) return false;
          const after = body[i + 1 + n.length];
          return after === undefined || !/\w/.test(after); // word boundary after
        })
      : undefined;
    if (name) {
      out.push({ userId: chosen[name], startOffset: i, endOffset: i + 1 + name.length });
      i = body.indexOf('@', i + 1 + name.length);
    } else {
      i = body.indexOf('@', i + 1);
    }
  }
  return out;
}

/**
 * The @mention suggestion list: a portaled listbox anchored under the
 * composer's textarea (flipping above near the viewport bottom), in the
 * Select/ContextMenu design language.
 */
function MentionPopup({ id, anchorRef, users, activeIndex, onHover, onPick, onDismiss }) {
  const panelRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current?.getBoundingClientRect();
    const panel = panelRef.current?.getBoundingClientRect();
    if (!anchor || !panel) return;
    const below = anchor.bottom + 4;
    const top =
      below + panel.height > window.innerHeight - 8 && anchor.top - panel.height - 4 > 8
        ? anchor.top - panel.height - 4
        : below;
    setPos({
      left: Math.max(8, Math.min(anchor.left, window.innerWidth - panel.width - 8)),
      top: Math.max(8, top),
    });
  }, [anchorRef, users]);

  useEffect(() => {
    const onPress = (e) => {
      if (!panelRef.current?.contains(e.target) && !anchorRef.current?.contains(e.target)) {
        onDismiss();
      }
    };
    const dismiss = (e) => {
      if (e?.target && panelRef.current?.contains(e.target)) return;
      onDismiss();
    };
    document.addEventListener('pointerdown', onPress);
    // Capture phase so scrolls inside nested containers dismiss too.
    document.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      document.removeEventListener('pointerdown', onPress);
      document.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [anchorRef, onDismiss]);

  // Keep the active option visible while navigating with the keyboard.
  useEffect(() => {
    if (activeIndex < 0) return;
    panelRef.current
      ?.querySelector(`#${CSS.escape(`${id}-opt-${activeIndex}`)}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [id, activeIndex]);

  return createPortal(
    <div
      ref={panelRef}
      id={`${id}-listbox`}
      role="listbox"
      style={pos ? { left: pos.left, top: pos.top } : { left: -9999, top: 0 }}
      className="fixed z-50 max-h-64 min-w-60 overflow-y-auto rounded-xl border border-slate-200 bg-slate-100 p-1.5 shadow-lg animate-scale-in"
    >
      {users.map((user, index) => (
        <button
          key={user.id}
          type="button"
          id={`${id}-opt-${index}`}
          role="option"
          aria-selected={index === activeIndex}
          onPointerEnter={() => onHover(index)}
          onPointerDown={(e) => e.preventDefault()} // keep the textarea focused
          onClick={() => onPick(user)}
          className={clsx(
            'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm text-slate-700 transition-colors',
            index === activeIndex && 'bg-slate-200',
          )}
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate">{user.displayName}</span>
            {user.email && (
              <span className="block truncate text-xs text-slate-400">{user.email}</span>
            )}
          </span>
        </button>
      ))}
    </div>,
    document.body,
  );
}

function Composer({ onSubmit, submitting, placeholder, compact }) {
  const composerId = useId();
  const textareaRef = useRef(null);
  const [body, setBody] = useState('');
  // Popup is open iff non-null: `token` is the text typed after "@", `atIndex`
  // is the offset of the "@" itself.
  const [mention, setMention] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // Users picked via the popup, displayName → userId; scanned at submit time.
  const [chosen, setChosen] = useState({});
  const dismissedRef = useRef(null); // { atIndex, token } Escape closed the popup on
  const pendingCaretRef = useRef(null); // caret to restore after an insertion renders

  const debouncedToken = useDebounced(mention?.token ?? '', 200);
  const { data: users = [] } = useQuery({
    queryKey: commentKeys.mentionable(debouncedToken),
    queryFn: () => mentionableUsers(debouncedToken),
    enabled: mention !== null,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const popupOpen = mention !== null && users.length > 0;
  const active = Math.min(activeIndex, users.length - 1);

  const syncMention = (event) => {
    if (event.nativeEvent?.isComposing) return; // IME safety
    const next = detectMentionToken(event.target.value, event.target.selectionStart);
    const dismissed = dismissedRef.current;
    // After Escape, stay closed on that exact token; typing changes the token
    // (or moves to another "@") and the popup comes back.
    if (next && dismissed && next.atIndex === dismissed.atIndex && next.token === dismissed.token) {
      return;
    }
    dismissedRef.current = null;
    if (next?.token !== mention?.token || next?.atIndex !== mention?.atIndex) setActiveIndex(0);
    setMention(next);
  };

  const accept = (user) => {
    const caret = textareaRef.current?.selectionStart ?? body.length;
    const inserted = `@${user.displayName} `;
    setChosen((prev) => ({ ...prev, [user.displayName]: user.id }));
    setBody(body.slice(0, mention.atIndex) + inserted + body.slice(caret));
    setMention(null);
    pendingCaretRef.current = mention.atIndex + inserted.length;
  };

  // Restore focus + caret after an accepted mention re-renders the textarea
  // (clicking a popup row would otherwise leave the caret at the end).
  useEffect(() => {
    if (pendingCaretRef.current == null) return;
    const el = textareaRef.current;
    el?.focus();
    el?.setSelectionRange(pendingCaretRef.current, pendingCaretRef.current);
    pendingCaretRef.current = null;
  }, [body]);

  const onKeyDown = (e) => {
    if (!popupOpen) return; // popup closed → every key behaves normally
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((active + dir + users.length) % users.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      accept(users[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation(); // don't close the host modal
      dismissedRef.current = { atIndex: mention.atIndex, token: mention.token };
      setMention(null);
    }
  };

  const submit = () => {
    if (!body.trim()) return;
    onSubmit({ body, mentions: deriveMentions(body, chosen) });
    setBody('');
    setChosen({});
    setMention(null);
  };

  return (
    <div className="space-y-2">
      <textarea
        ref={textareaRef}
        rows={compact ? 2 : 3}
        value={body}
        placeholder={placeholder}
        onChange={(e) => {
          setBody(e.target.value);
          syncMention(e);
        }}
        onSelect={syncMention}
        onKeyDown={onKeyDown}
        aria-autocomplete="list"
        aria-expanded={popupOpen}
        aria-controls={popupOpen ? `${composerId}-listbox` : undefined}
        aria-activedescendant={popupOpen ? `${composerId}-opt-${active}` : undefined}
        className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      />
      {popupOpen && (
        <MentionPopup
          id={composerId}
          anchorRef={textareaRef}
          users={users}
          activeIndex={active}
          onHover={setActiveIndex}
          onPick={accept}
          onDismiss={() => setMention(null)}
        />
      )}
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-slate-400">Type @ to mention a member</p>
        <Button size="sm" onClick={submit} loading={submitting}>
          {placeholder?.startsWith('Reply') ? 'Reply' : 'Comment'}
        </Button>
      </div>
    </div>
  );
}

/** Render the body, highlighting each @mention span (offsets from the API). */
function renderBody(body, mentions) {
  const spans = (mentions ?? [])
    .filter((m) => m.startOffset >= 0 && m.endOffset <= body.length && m.endOffset > m.startOffset)
    .sort((a, b) => a.startOffset - b.startOffset);
  if (!spans.length) return body;
  const out = [];
  let cursor = 0;
  spans.forEach((m, i) => {
    if (m.startOffset < cursor) return; // skip overlaps
    if (m.startOffset > cursor) out.push(body.slice(cursor, m.startOffset));
    out.push(
      <span
        key={i}
        title={m.displayName ?? undefined}
        className="rounded bg-brand-50 px-1 font-medium text-brand-700"
      >
        {body.slice(m.startOffset, m.endOffset)}
      </span>,
    );
    cursor = m.endOffset;
  });
  if (cursor < body.length) out.push(body.slice(cursor));
  return out;
}

function CommentBody({ comment }) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-slate-700">
          {comment.author?.displayName ?? 'Unknown'}
        </span>
        <span className="text-xs text-slate-400">{formatDate(comment.createdAt)}</span>
        {comment.editedAt && <span className="text-xs text-slate-300">(edited)</span>}
        {comment.isPinned && <Pin className="h-3 w-3 text-amber-500" />}
        {comment.isResolved && <CheckCircle2 className="h-3 w-3 text-green-600" />}
      </div>
      <p className="whitespace-pre-wrap text-sm text-slate-600">
        {renderBody(comment.body, comment.mentions)}
      </p>
    </div>
  );
}

/**
 * The full comments experience for one target (composer, threads, replies,
 * resolve/pin/edit/delete). Hosted by CommentsModal (unchanged behavior) and
 * the file viewer's conversation sidebar (`variant="sidebar"` fills its
 * parent's height instead of capping the list). Only mount it when visible —
 * both hosts already do.
 */
export default function CommentsPanel({ targetType, targetId, variant = 'modal' }) {
  const queryClient = useQueryClient();
  const meId = useAuthStore((s) => s.user?.id);
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editBody, setEditBody] = useState('');
  const sidebar = variant === 'sidebar';

  const { data, isLoading } = useQuery({
    queryKey: commentKeys.list(targetType, targetId),
    queryFn: () => listComments(targetType, targetId),
    enabled: Boolean(targetId),
  });
  const roots = data?.data ?? [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: commentKeys.list(targetType, targetId) });
    if (expanded) queryClient.invalidateQueries({ queryKey: commentKeys.thread(expanded) });
  };
  const run = (fn, ok) =>
    fn
      .then(() => {
        invalidate();
        if (ok) toast.success(ok);
      })
      .catch((err) => toast.error(getErrorMessage(err, 'Action failed')));

  const createMutation = useMutation({
    mutationFn: createComment,
    onSuccess: () => invalidate(),
    onError: (err) => toast.error(getErrorMessage(err, 'Could not comment')),
  });

  const thread = useQuery({
    queryKey: commentKeys.thread(expanded),
    queryFn: () => getThread(expanded),
    enabled: Boolean(expanded),
  });
  const replies = (thread.data ?? []).filter((c) => c.id !== expanded);

  const submitEdit = (id) => {
    if (!editBody.trim()) return;
    run(updateComment({ id, body: editBody }), 'Comment updated').then(() => setEditing(null));
  };

  return (
    <div className={sidebar ? 'flex h-full min-h-0 flex-col gap-3' : 'space-y-4'}>
      {sidebar && (
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          Comments ({data?.meta?.total ?? roots.length})
        </p>
      )}
      <Composer
        submitting={createMutation.isPending}
        placeholder="Add a comment…"
        compact={sidebar}
        onSubmit={(payload) => createMutation.mutate({ targetType, targetId, ...payload })}
      />

      {isLoading ? (
        <div className="py-8">
          <Spinner />
        </div>
      ) : roots.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">No comments yet.</p>
      ) : (
        <ul
          className={
            sidebar
              ? 'scrollbar-thin min-h-0 flex-1 space-y-3 overflow-auto'
              : 'max-h-96 space-y-3 overflow-auto'
          }
        >
          {roots.map((c) => (
            <li key={c.id} className="rounded-lg border border-slate-100 p-3">
              {editing === c.id ? (
                <div className="space-y-2">
                  <textarea
                    rows={2}
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                    className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => submitEdit(c.id)}>
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <CommentBody comment={c} />
              )}

              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-400">
                <button
                  className="hover:text-brand-600"
                  onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                >
                  <MessageSquare className="mr-1 inline h-3 w-3" />
                  {c.replyCount} {c.replyCount === 1 ? 'reply' : 'replies'}
                </button>
                <button
                  className="hover:text-brand-600"
                  onClick={() =>
                    run(
                      c.isResolved ? reopenComment(c.id) : resolveComment(c.id),
                      c.isResolved ? 'Reopened' : 'Resolved',
                    )
                  }
                >
                  {c.isResolved ? 'Reopen' : 'Resolve'}
                </button>
                <button
                  className="hover:text-brand-600"
                  onClick={() => run(c.isPinned ? unpinComment(c.id) : pinComment(c.id))}
                >
                  <Pin className="mr-1 inline h-3 w-3" />
                  {c.isPinned ? 'Unpin' : 'Pin'}
                </button>
                {c.author?.id === meId && (
                  <>
                    <button
                      className="hover:text-brand-600"
                      onClick={() => {
                        setEditing(c.id);
                        setEditBody(c.body);
                      }}
                    >
                      <Pencil className="mr-1 inline h-3 w-3" />
                      Edit
                    </button>
                    <button
                      className="hover:text-red-600"
                      onClick={() => run(deleteComment(c.id), 'Deleted')}
                    >
                      <Trash2 className="mr-1 inline h-3 w-3" />
                      Delete
                    </button>
                  </>
                )}
              </div>

              {expanded === c.id && (
                <div className="mt-3 space-y-3 border-l-2 border-slate-100 pl-3">
                  {replies.map((r) => (
                    <CommentBody key={r.id} comment={r} />
                  ))}
                  <Composer
                    compact
                    submitting={createMutation.isPending}
                    placeholder="Reply…"
                    onSubmit={(payload) =>
                      createMutation.mutate({
                        targetType,
                        targetId,
                        parentCommentId: c.id,
                        ...payload,
                      })
                    }
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
